/* =============================================================================
  File: assets/component-product-gallery.js
  PurrGallery (Glide) — CLEAN VERSION
  - Groups media by cover images (cover = image whose alt matches a color label)
  - An alt's color key is the text before its first "|" ("Grey | 3-In-1 Cave in Grey: ...");
    an alt with no "|" is its own key
  - Filters to selected color on variant change
  - Prepends universal "All" cover image (alt="All") to every variant gallery
  - Smooth fade/scale transitions between slides + after rebuild
============================================================================= */
(() => {
  const INIT_ATTR = 'data-purr-init';
  const STATE_KEY = '__purrGalleryState';
  const NS = '[PurrGallery]';

  /* --------------------------------
     Glide loader
  -------------------------------- */
  function waitForGlide(cb, tries = 0) {
    if (typeof window.Glide === 'function') return cb(true);
    if (tries > 120) return cb(false);
    setTimeout(() => waitForGlide(cb, tries + 1), 50);
  }

  /* --------------------------------
     Theme fullscreen/zoom hooks
  -------------------------------- */
  function openFullscreen(idx) {
    const i = Number(idx) || 0;
    try {
      if (window.PURRZ?.openModalMedia) return window.PURRZ.openModalMedia(i);
      if (window.PubSub?.publish) return window.PubSub.publish('modal-media', i);
    } catch (_) {}
  }

  function openZoom(idx) {
    const i = Number(idx) || 0;
    try {
      if (window.PURRZ?.openZoomMedia) return window.PURRZ.openZoomMedia(i);
      if (window.PURRZ?.openImageZoom) return window.PURRZ.openImageZoom(i);
      if (window.PubSub?.publish) return window.PubSub.publish('zoom-media', i);
    } catch (_) {}
    openFullscreen(i);
  }

  /* --------------------------------
     Smooth transitions (JS-driven)
  -------------------------------- */
  const FX = {
    duration: 360,
    easing: 'cubic-bezier(0.22, 0.61, 0.36, 1)',
    inOpacity: 1,
    outOpacity: 0,
    inScale: 1,
    outScale: 0.985,
  };

  function getSlideImg(slide) {
    return slide?.querySelector('.purr-gallery-main-image, img');
  }

  function primeFX(root) {
    const slides = [...root.querySelectorAll('.glide__slide')];
    slides.forEach((s) => {
      const img = getSlideImg(s);
      if (!img) return;

      img.style.willChange = 'opacity, transform';
      img.style.transition = `opacity ${FX.duration}ms ${FX.easing}, transform ${FX.duration}ms ${FX.easing}`;
      img.style.transformOrigin = 'center';

      const active = s.classList.contains('glide__slide--active');
      img.style.opacity = active ? String(FX.inOpacity) : String(FX.outOpacity);
      img.style.transform = active ? `scale(${FX.inScale})` : `scale(${FX.outScale})`;
    });
  }

  function setActiveFX(root, activeIdx) {
    const slides = [...root.querySelectorAll('.glide__slide')];
    slides.forEach((s, i) => {
      const img = getSlideImg(s);
      if (!img) return;
      img.style.opacity = i === activeIdx ? String(FX.inOpacity) : String(FX.outOpacity);
      img.style.transform = i === activeIdx ? `scale(${FX.inScale})` : `scale(${FX.outScale})`;
    });
  }

  /* --------------------------------
     Helpers
  -------------------------------- */
  function norm(s) {
    return (s || '').toString().replace(/\s+/g, ' ').trim();
  }

  function normLc(s) {
    return norm(s).toLowerCase();
  }

  function readAlt(slideEl) {
    const img = slideEl?.querySelector('.purr-gallery-main-image, img');
    const alt = img?.getAttribute('data-media-alt') || img?.getAttribute('alt') || '';
    return norm(alt.split('|')[0]);
  }

  function getSelectedColor(root) {
    const scope = root?.closest('s-main-product') || root?.closest('#MainContent') || document;

    const checked =
      scope.querySelector('.s-main-product__variant-picker .c-variant-picker__item--color input[type="radio"]:checked') ||
      scope.querySelector('.c-variant-picker__is-product-section .c-variant-picker__item--color input[type="radio"]:checked') ||
      scope.querySelector('.c-variant-picker__item--color input[type="radio"]:checked');

    if (!checked) return '';
    const label = checked.closest('.c-variant-picker__item--color');
    return label?.querySelector('.c-variant-picker__text')?.textContent?.trim() || '';
  }

  function getCoverNames(root) {
    const scope = root.closest('s-main-product') || document;
    const names = [...scope.querySelectorAll('.c-variant-picker__item--color .c-variant-picker__text')]
      .map((el) => el.textContent.trim())
      .filter(Boolean);
    return [...new Set(names)].map((n) => normLc(n));
  }

  function collectOriginalItems(root) {
    const slides = [...root.querySelectorAll('.glide__slide')];
    const thumbs = [...root.querySelectorAll('.purr-gallery-thumb')];

    return slides.map((slide, i) => {
      const thumb = thumbs[i] || null;
      return {
        originalIndex: i,
        alt: readAlt(slide),
        slideHTML: slide.outerHTML,
        thumbHTML: thumb ? thumb.outerHTML : '',
      };
    });
  }

  function buildGroups(items, coverNamesLower) {
    const coverSet = new Set(coverNamesLower || []);
    const groups = new Map();
    let currentKey = '';

    items.forEach((it) => {
      const a = norm(it.alt);
      const aLc = a.toLowerCase();

      // "All" is a special cover that should always start a group
      const isAllCover = aLc === 'all';

      // New group starts when alt matches a color name OR alt="All"
      if (a && (isAllCover || coverSet.has(aLc))) {
        currentKey = a;
        if (!groups.has(currentKey)) groups.set(currentKey, []);
        groups.get(currentKey).push(it);
        return;
      }

      // Add to current group
      if (currentKey) {
        groups.get(currentKey).push(it);
        return;
      }

      // Fallback bucket before first cover
      const fallback = '__ungrouped__';
      if (!groups.has(fallback)) groups.set(fallback, []);
      groups.get(fallback).push(it);
    });

    return [...groups.entries()].map(([alt, arr]) => ({ alt, items: arr }));
  }

  function pickGroup(groups, color) {
    const target = norm(color);
    if (!groups?.length) return null;
    if (!target) return groups[0];

    const exact = groups.find((g) => norm(g.alt) === target);
    if (exact) return exact;

    const loose = groups.find((g) => normLc(g.alt) === normLc(target));
    if (loose) return loose;

    return groups[0];
  }

  function getUniversalAllPrefixItem(groups) {
    // Find group with alt="All" (case-insensitive). Use ONLY the first image.
    const allGroup = (groups || []).find((g) => normLc(g.alt) === 'all');
    if (!allGroup?.items?.length) return null;
    return allGroup.items[0] || null;
  }

  function withAllPrefix(st, chosenItems) {
    const prefix = st?.allPrefixItem ? [st.allPrefixItem] : [];
    const seen = new Set();

    const out = [];
    [...prefix, ...(chosenItems || [])].forEach((it) => {
      if (!it) return;
      const key = String(it.originalIndex);
      if (seen.has(key)) return;
      seen.add(key);
      out.push(it);
    });

    return out;
  }

  function rebuildDOM(root, itemsToShow) {
    const glideRoot = root.querySelector('.glide');
    const slidesUL = glideRoot?.querySelector('.glide__slides');
    const thumbsWrap = root.querySelector('.purr-gallery-thumbs');
    if (!glideRoot || !slidesUL || !thumbsWrap) return;

    slidesUL.innerHTML = (itemsToShow || [])
      .map((it, idx) => {
        const tmp = document.createElement('div');
        tmp.innerHTML = it.slideHTML;
        const li = tmp.firstElementChild;
        if (!li) return it.slideHTML;

        li.setAttribute('data-index', String(idx));
        li.setAttribute('data-original-index', String(it.originalIndex));

        const fsBtn = li.querySelector('.js-purr-gallery-fullscreen');
        if (fsBtn) {
          fsBtn.setAttribute('data-index', String(idx));
          fsBtn.setAttribute('data-original-index', String(it.originalIndex));
        }

        const img = li.querySelector('.purr-gallery-main-image, img');
        if (img) img.setAttribute('data-original-index', String(it.originalIndex));

        return li.outerHTML;
      })
      .join('');

    thumbsWrap.innerHTML = (itemsToShow || [])
      .map((it, idx) => {
        if (it.thumbHTML) {
          const tmp = document.createElement('div');
          tmp.innerHTML = it.thumbHTML;
          const btn = tmp.firstElementChild;
          if (!btn) return it.thumbHTML;

          btn.setAttribute('data-thumb-index', String(idx));
          btn.setAttribute('data-original-index', String(it.originalIndex));
          return btn.outerHTML;
        }

        // Fallback thumb from slide image
        const tmp = document.createElement('div');
        tmp.innerHTML = it.slideHTML;
        const img = tmp.querySelector('.purr-gallery-main-image, img');

        const src = img?.getAttribute('src') || '';
        const alt = img?.getAttribute('data-media-alt') || img?.getAttribute('alt') || '';

        return `
          <button class="purr-gallery-thumb" data-thumb-index="${idx}" data-original-index="${it.originalIndex}" type="button">
            <img class="purr-gallery-thumb-image" src="${src}" alt="${alt}">
          </button>
        `;
      })
      .join('');
  }

  /* --------------------------------
     Sync popup modal gallery
  -------------------------------- */
  function rebuildModalMedia(root, itemsToShow) {
    const modalWrapper = document.querySelector('.c-modal-media__wrapper');
    if (!modalWrapper) return;

    // Store original popup slides once
    if (!window.__PurrOriginalModalSlides) {
      window.__PurrOriginalModalSlides = [
        ...modalWrapper.querySelectorAll('.c-modal-media__slide')
      ].map((slide) => slide.outerHTML);
    }

    const originalSlides = window.__PurrOriginalModalSlides;

    const html = (itemsToShow || [])
      .map((it) => {
        return originalSlides[it.originalIndex] || '';
      })
      .join('');

    modalWrapper.innerHTML = html;

    // Update progress total
    const progress = document.querySelector('.c-modal-media__progress');
    if (progress) {
      const totalTextNode = progress.childNodes[2];
      if (totalTextNode) {
        totalTextNode.textContent = ` / ${itemsToShow.length}`;
      }
    }

    // Re-init swiper if theme has one
    try {
      const swiperEl = document.querySelector('.js-modal-media__swiper');

      if (swiperEl?.swiper) {
        swiperEl.swiper.update();
        swiperEl.swiper.slideTo(0, 0);
      }
    } catch (e) {
      console.warn(`${NS} Modal swiper update failed`, e);
    }

    console.log(`${NS} Modal media synced (${itemsToShow.length} slides)`);
  }

  /* --------------------------------
     Destroy / Bind
  -------------------------------- */
  function destroy(root) {
    const st = root?.[STATE_KEY];
    if (!st) return;

    try {
      st.handlers?.forEach(({ el, type, fn, opts }) => el.removeEventListener(type, fn, opts));
    } catch (_) {}
    try {
      st.glide?.destroy();
    } catch (_) {}

    root[STATE_KEY] = undefined;
    root.removeAttribute(INIT_ATTR);
  }

  function bind(root, glide) {
    const handlers = [];
    const remember = (el, type, fn, opts) => {
      el.addEventListener(type, fn, opts);
      handlers.push({ el, type, fn, opts });
    };

    const slides = [...root.querySelectorAll('.glide__slide')];
    const thumbs = [...root.querySelectorAll('.purr-gallery-thumb')];

    const markActive = (i) => {
      thumbs.forEach((t) => t.classList.remove('purr-gallery-thumb--active'));
      const active = thumbs.find((t) => Number(t.dataset.thumbIndex) === Number(i));
      active?.classList.add('purr-gallery-thumb--active');
    };

    // FX
    primeFX(root);
    setActiveFX(root, Number(glide.index || 0));

    glide.on('run.before', () => {
      setActiveFX(root, Number(glide.index || 0));
    });

    glide.on(['mount.after', 'run.after'], () => {
      const idx = Number(glide.index || 0);
      markActive(idx);
      setActiveFX(root, idx);
    });

    // Main image click -> fullscreen (use original)
    slides.forEach((slide) => {
      const idx = Number(slide.dataset.index || 0);

      const img = slide.querySelector('.purr-gallery-main-image');
      if (!img) return;

      remember(
        img,
        'click',
        () => {
          glide.go('=' + idx);

          // sync modal swiper first
          const modalSwiper = document.querySelector('.js-modal-media__swiper')?.swiper;

          if (modalSwiper) {
            modalSwiper.slideTo(idx, 0);
          }

          openFullscreen(idx);
        },
        { passive: true }
      );
    });

    // Fullscreen buttons
    [...root.querySelectorAll('.js-purr-gallery-fullscreen')].forEach((btn) => {
      const idx = Number(btn.dataset.index || 0);

      remember(btn, 'click', (e) => {
        e.preventDefault();

        glide.go('=' + idx);

        // sync popup swiper
        const modalSwiper = document.querySelector('.js-modal-media__swiper')?.swiper;

        if (modalSwiper) {
          modalSwiper.slideTo(idx, 0);
        }

        openFullscreen(idx);
      });
    });

    // Zoom buttons
    [...root.querySelectorAll('.js-purr-gallery-zoom, .purr-gallery-zoom, [data-purr-zoom]')].forEach((btn) => {
      const idx = Number(btn.dataset.index || btn.getAttribute('data-index') || glide.index || 0);
      const originalIdx = Number(btn.dataset.originalIndex || idx);

      remember(btn, 'click', (e) => {
        e.preventDefault();
        glide.go('=' + idx);
        openZoom(originalIdx);
      });
    });

    // Thumbs click
    thumbs.forEach((th) => {
      const idx = Number(th.dataset.thumbIndex || 0);
      remember(th, 'click', () => glide.go('=' + idx));
    });

    // Arrows
    const leftArrow = root.querySelector('.glide__arrow--left');
    const rightArrow = root.querySelector('.glide__arrow--right');
    if (leftArrow)
      remember(leftArrow, 'click', (e) => {
        e.preventDefault();
        glide.go('<');
      });
    if (rightArrow)
      remember(rightArrow, 'click', (e) => {
        e.preventDefault();
        glide.go('>');
      });

    markActive(0);
    return handlers;
  }

  /* --------------------------------
     Apply filter
  -------------------------------- */
  function applyFilter(root, forcedColor = '') {
    const st = root?.[STATE_KEY];
    if (!st?.originalItems?.length) return;

    const color = norm(forcedColor || getSelectedColor(root));
    const chosenGroup = pickGroup(st.groups, color);
    if (!chosenGroup) return;

    if (st.activeAlt && norm(st.activeAlt) === norm(chosenGroup.alt)) return;
    st.activeAlt = chosenGroup.alt;

    // teardown
    try {
      st.handlers?.forEach(({ el, type, fn, opts }) => el.removeEventListener(type, fn, opts));
    } catch (_) {}
    try {
      st.glide?.destroy();
    } catch (_) {}

    // build filtered list + prepend "All" cover (first image)
    const itemsToShow = withAllPrefix(st, chosenGroup.items);

    // rebuild + mount
    rebuildDOM(root, itemsToShow);
    rebuildModalMedia(root, itemsToShow);

    const glideRoot = root.querySelector('.glide');
    const glide = new window.Glide(glideRoot, { type: 'slider', perView: 1, gap: 0, bound: true, rewind: false });
    glide.mount();

    st.glide = glide;
    st.handlers = bind(root, glide);

    requestAnimationFrame(() => {
      primeFX(root);
      setActiveFX(root, Number(glide.index || 0));
    });

    console.log(`${NS} Filter applied -> ${chosenGroup.alt} (+All prefix) (${itemsToShow.length} images)`);
  }

  /* --------------------------------
     Init / Scan
  -------------------------------- */
  function init(root) {
    if (!root || root.getAttribute(INIT_ATTR) === '1') return;

    const glideRoot = root.querySelector('.glide');
    if (!glideRoot) return;

    waitForGlide((ok) => {
      if (!ok || !root.isConnected) return;

      if (root[STATE_KEY]) destroy(root);

      const originalItems = collectOriginalItems(root);
      const coverNamesLower = getCoverNames(root);
      const groups = buildGroups(originalItems, coverNamesLower);

      const allPrefixItem = getUniversalAllPrefixItem(groups);

      root[STATE_KEY] = {
        glide: null,
        handlers: [],
        originalItems,
        groups,
        activeAlt: '',
        allPrefixItem, // <-- IMPORTANT
      };

      const meaningful = groups.filter((g) => g.alt && g.alt !== '__ungrouped__');
      const shouldFilter = meaningful.length > 1;

      // initial build (filtered if possible)
      if (shouldFilter) {
        const initialColor = getSelectedColor(root);
        const chosenGroup = pickGroup(groups, initialColor) || groups[0];

        root[STATE_KEY].activeAlt = chosenGroup?.alt || '';

        const itemsToShow = withAllPrefix(root[STATE_KEY], chosenGroup?.items || originalItems);
        rebuildDOM(root, itemsToShow);
        rebuildModalMedia(root, itemsToShow);
      }

      const glide = new window.Glide(glideRoot, { type: 'slider', perView: 1, gap: 0, bound: true, rewind: false });
      try {
        glide.mount();
      } catch (e) {
        console.error(`${NS} Glide mount failed`, e);
        return;
      }

      root[STATE_KEY].glide = glide;
      root[STATE_KEY].handlers = bind(root, glide);
      root.setAttribute(INIT_ATTR, '1');

      requestAnimationFrame(() => {
        primeFX(root);
        setActiveFX(root, Number(glide.index || 0));
      });

      console.log(`${NS} init OK`, root, 'groups:', meaningful.length || groups.length);
      if (shouldFilter) console.log(`${NS} Initial -> ${root[STATE_KEY].activeAlt} (+All prefix)`);
      if (allPrefixItem) console.log(`${NS} All prefix enabled`);
    });
  }

  function scan(ctx = document) {
    ctx.querySelectorAll(`.purr-gallery:not([${INIT_ATTR}="1"])`).forEach(init);
  }

  document.addEventListener('DOMContentLoaded', () => scan(document));
  document.addEventListener('shopify:section:load', (ev) => scan(ev.target || document));

  // MutationObserver: init/destroy safety
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      if (m.type !== 'childList') continue;

      m.addedNodes?.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches?.('.purr-gallery')) init(node);
        else if (node.querySelectorAll) scan(node);
      });

      m.removedNodes?.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node[STATE_KEY]) destroy(node);
        node.querySelectorAll?.('.purr-gallery').forEach(destroy);
      });
    }
  });
  mo.observe(document.documentElement, { childList: true, subtree: true });

  /* --------------------------------
     Variant change hooks
  -------------------------------- */
  document.addEventListener('change', (e) => {
    const t = e.target;
    if (!t) return;

    if (t.matches('.c-variant-picker__item--color input[type="radio"]')) {
      const label = t.closest('.c-variant-picker__item--color');
      const clickedColor = label?.querySelector('.c-variant-picker__text')?.textContent?.trim() || '';

      const scope = t.closest('s-main-product') || t.closest('#MainContent') || document;

      setTimeout(() => {
        scope.querySelectorAll('.purr-gallery[data-purr-init="1"]').forEach((root) => {
          applyFilter(root, clickedColor);
        });
      }, 0);
    }
  });

  function hookPubSubVariantUpdate() {
    if (!window.PubSub?.subscribe) return false;

    window.PubSub.subscribe('variant:update', () => {
      setTimeout(() => {
        document.querySelectorAll('.purr-gallery[data-purr-init="1"]').forEach((root) => {
          applyFilter(root);
        });
      }, 0);
    });

    return true;
  }

  if (!hookPubSubVariantUpdate()) {
    let tries = 0;
    const iv = setInterval(() => {
      tries += 1;
      if (hookPubSubVariantUpdate() || tries > 60) clearInterval(iv);
    }, 100);
  }

  window.PurrGallery = { scan, init, destroy, applyVariantFilter: applyFilter, readAlt, buildGroups };
})();
